import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSummaryCapture } from "@/hooks/voice/useSummaryCapture";
import type { ContactDetailsRequestHandler, SummaryCaptureValues } from "@/types/voice/summary-capture";

/** After this many user turns, an engaged conversation earns the inline capture moment. */
const MIN_USER_TURNS = 3;
/** ...or after this long, whichever comes first. */
const MIN_ELAPSED_MS = 90_000;
const POLL_MS = 5_000;
/** An agent turn this long (following a user turn) counts as a "real answer" value moment. */
const MIN_ANSWER_CHARS = 200;
/**
 * Once armed, the ask still waits for a quiet beat: the Genie has finished its
 * turn and nothing has moved in the conversation for this long. It must never
 * land mid-answer (Sam, 24 Sep 2026: it popped in while the Genie was still
 * replying and read as part of the reply).
 */
const SETTLE_MS = 2_500;
const SETTLE_POLL_MS = 500;

/** The visitor has something half-typed in a text field; the ask can wait. */
function isVisitorTyping(): boolean {
  const el = typeof document === "undefined" ? null : document.activeElement;
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return false;
  return el.value.trim().length > 0;
}

/** A resource the Genie just surfaced mid-call — arms the moment immediately, named. */
export interface SurfacedResourceLike {
  id: string;
  label: string;
}

interface UseInConversationCaptureOptions {
  connected: boolean;
  /** `message` is optional so callers with a looser transcript shape still fit. */
  messages: Array<{ isUser: boolean; message?: string }>;
  conversationStartTime: number | null;
  agentId?: string;
  agentUserId?: string;
  /** The resource the Genie just named/linked, if any (see useSurfacedResource). */
  surfacedResource?: SurfacedResourceLike | null;
  /** The Genie is speaking, thinking or looking something up. The ask waits for it to finish. */
  agentBusy?: boolean;
  /** The live conversation, saved with the email so the recap goes out without telling the Genie. */
  conversationId?: string | null;
  onCaptured?: (email: string) => void;
}

/**
 * Drives the in-conversation "want me to email you this?" moment. Arms at the
 * first genuine value moment in a conversation — the Genie surfacing a
 * resource, or giving its first substantive answer — so the ask lands right
 * after the visitor has received something worth keeping, not on an arbitrary
 * timer. Turn-count / elapsed-time fallbacks still cover conversations that
 * never hit either signal. Once armed it stays armed for the rest of that
 * conversation; dismissing it, or a new conversation starting, resets the ask
 * so it never nags twice in one call. An already-captured visitor still gets
 * the moment (it renders a quiet "Recap goes to…" confirmation instead of the
 * ask — see useSummaryCapture/SummaryCaptureForm) rather than being hidden.
 *
 * It is a side note, never part of the conversation: nothing here is sent to
 * the Genie, the ask only appears once the Genie is done talking and the
 * visitor is not mid-sentence, and it never takes the cursor.
 */
export const useInConversationCapture = ({
  connected,
  messages,
  conversationStartTime,
  agentId,
  agentUserId,
  surfacedResource,
  agentBusy = false,
  conversationId,
  onCaptured,
}: UseInConversationCaptureOptions) => {
  const [armed, setArmed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Armed says the conversation has earned the ask; settled says now is a
  // polite moment to show it.
  const [settled, setSettled] = useState(false);
  const busyRef = useRef(agentBusy);
  busyRef.current = agentBusy;
  const quietSinceRef = useRef(Date.now());
  // Snapshot of whichever resource triggered the arm, so the copy stays
  // stable even if the surfaced-resource highlight clears seconds later.
  const [armedResource, setArmedResource] = useState<SurfacedResourceLike | null>(null);
  // The Genie asked for the visitor's details (requestContactDetails) on
  // buying intent: the moment becomes a contact ask, name and phone included.
  const [contactRequested, setContactRequested] = useState(false);

  const capture = useSummaryCapture({
    agentId,
    agentUserId,
    source: "in_conversation",
    conversationId,
    onCaptured,
  });

  // A fresh conversation gets a fresh chance at the moment.
  useEffect(() => {
    if (connected) {
      setArmed(false);
      setDismissed(false);
      setSettled(false);
      setArmedResource(null);
      setContactRequested(false);
    }
  }, [connected]);

  // The tool call answers synchronously, so it reads the latest state from a ref.
  const requestStateRef = useRef({ connected, agentId, dismissed, capture });
  requestStateRef.current = { connected, agentId, dismissed, capture };

  const requestContactDetails = useCallback<ContactDetailsRequestHandler>((prefill) => {
    const state = requestStateRef.current;
    if (state.capture.submitted || (state.capture.capturedEmail && !state.capture.editing)) return "already_captured";
    if (!state.connected || !state.agentId || state.dismissed) return "declined";

    const patch: Partial<SummaryCaptureValues> = {};
    for (const field of ["email", "name", "phone"] as const) {
      if (prefill[field] && !state.capture.values[field]?.trim()) patch[field] = prefill[field];
    }
    if (Object.keys(patch).length > 0) state.capture.onChange(patch);
    setContactRequested(true);
    setArmed(true);
    return "shown";
  }, []);

  // Any movement restarts the quiet clock: the Genie starting or stopping, or a
  // new or growing message (a streamed reply changes the list on every chunk).
  useEffect(() => {
    quietSinceRef.current = Date.now();
  }, [agentBusy, messages]);

  useEffect(() => {
    if (!connected || !armed || dismissed || settled) return;
    const id = window.setInterval(() => {
      const quietFor = Date.now() - quietSinceRef.current;
      if (!busyRef.current && quietFor >= SETTLE_MS && !isVisitorTyping()) setSettled(true);
    }, SETTLE_POLL_MS);
    return () => window.clearInterval(id);
  }, [connected, armed, dismissed, settled]);

  const userTurns = messages.filter((m) => m.isUser).length;

  // Value moment 1: the Genie just surfaced a resource — the ask can name it.
  useEffect(() => {
    if (!connected || armed || dismissed || !surfacedResource) return;
    setArmed(true);
    setArmedResource(surfacedResource);
  }, [connected, armed, dismissed, surfacedResource]);

  // Value moment 2: the Genie's first substantive answer (a real paragraph,
  // not a one-liner) following a user turn.
  const hasSubstantiveAnswer = useMemo(() => {
    let sawUserTurn = false;
    for (const m of messages) {
      if (m.isUser) {
        sawUserTurn = true;
        continue;
      }
      if (sawUserTurn && (m.message?.length ?? 0) >= MIN_ANSWER_CHARS) return true;
    }
    return false;
  }, [messages]);

  useEffect(() => {
    if (!connected || armed || dismissed) return;
    if (hasSubstantiveAnswer) setArmed(true);
  }, [connected, armed, dismissed, hasSubstantiveAnswer]);

  // Fallback 1: engaged by turn count.
  useEffect(() => {
    if (!connected || armed || dismissed) return;
    if (userTurns >= MIN_USER_TURNS) setArmed(true);
  }, [connected, armed, dismissed, userTurns]);

  // Fallback 2: engaged by elapsed time.
  useEffect(() => {
    if (!connected || armed || dismissed || !conversationStartTime) return;
    const id = window.setInterval(() => {
      if (Date.now() - conversationStartTime >= MIN_ELAPSED_MS) setArmed(true);
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [connected, armed, dismissed, conversationStartTime]);

  // An already-captured visitor still gets the moment — it just renders as a
  // quiet confirmation instead of an ask (see SummaryCaptureForm) — so this no
  // longer excludes capture.alreadyCaptured.
  const visible = connected && armed && settled && !dismissed && !!agentId;

  useEffect(() => {
    if (visible) capture.markShown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return {
    visible,
    dismiss: () => setDismissed(true),
    capture,
    /** The resource named in the ask/confirmation copy, if this moment was armed by one. */
    resourceLabel: armedResource?.label ?? null,
    /** The Genie asked for contact details; renders the contact ask instead of the recap ask. */
    contactRequested,
    /** Handler for the Genie's requestContactDetails tool (useVoiceAgent's onContactDetailsRequested). */
    requestContactDetails,
  };
};
